import { useCallback, useEffect, useState } from "react";
import loader from "../../../../../components/recharts/loader.gif";
import { ChevronUp, ChevronDown } from "lucide-react";
import { getBessResultSummary } from "../../../../../Rest_api/RestApi";

interface CostScenarioSummaryData {
  scenario_id: number;
  scenario_number: number;
  period: string;
  re_gen_cost: string | number;
  non_re_gen_cost: string | number;
  cost_before_bess: string | number;
  cost_after_bess: string | number;
}

interface YearlyCostData {
  year: number;
  period: string;
  re_gen_cost: string | number;
  non_re_gen_cost: string | number;
  cost_before_bess: string | number;
  cost_after_bess: string | number;
}

interface MonthlyCostData {
  year: number;
  month: number;
  month_name: string;
  period: string;
  re_gen_cost: string | number;
  non_re_gen_cost: string | number;
  cost_before_bess: string | number;
  cost_after_bess: string | number;
}

const HeaderCell: React.FC<{ title: React.ReactNode; className?: string }> = ({ title, className = "" }) => (
  <th className={`py-2 text-xs px-2 text-center font-medium text-[#F1935C] leading-tight ${className}`}>
    {title}
  </th>
);

const HeaderCell2: React.FC<{ title: React.ReactNode }> = ({ title }) => (
  <th className="py-2 text-xs px-2 text-center font-medium text-[#34656D] !bg-[#F7FAFF] leading-tight">
    {title}
  </th>
);

export default function CostSummaryTable({
  refreshKey,
  setError,
  selectedYears,
  setSelectedYears,
  setAvailableYears,
  selectedScenarios,
}: {
  refreshKey: number;
  setError?: any;
  selectedYears: any[];
  setSelectedYears: (years: any[]) => void;
  setAvailableYears: (years: any[]) => void;
  selectedScenarios: Array<{ scenario_id: number, scenario_number: number }>;
}) {
  const [data, setData] = useState<CostScenarioSummaryData[]>([]);
  const [scenariosData, setScenariosData] = useState<any>({});
  const [fullResponse, setFullResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  // Helper function to parse numeric values
  const parseValue = (value: string | number): number => {
    if (typeof value === "number") return value;
    return parseFloat(value) || 0;
  };

  // Helper function to format currency
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Fetch cost data from API
  const handleFetchData = useCallback(async () => {
    try {
      setLoading(true);
      const scenarioIds = selectedScenarios?.map((s) => s.scenario_id) || [];
      const response = await getBessResultSummary(scenarioIds);



      if (response && response.data_scenariowise) {
        setFullResponse(response);
        setScenariosData(response.scenarios || {});
        setData(response.data_scenariowise);

        // Extract available years
        const uniqueYears = new Set<number>();
        if (response.scenarios) {
          Object.values(response.scenarios).forEach((scenario: any) => {
            scenario.data_yearwise?.forEach((item: any) => uniqueYears.add(item.year));
          });
        }

        const yearOptions = Array.from(uniqueYears).sort().map((year) => ({
          value: year,
          label: year.toString(),
        }));
        setAvailableYears(yearOptions);

      } else {
        console.error("Invalid response format:", response);
        setError?.("Invalid data format received from API.");
      }
    } catch (error) {
      console.error("Error fetching cost data", error);
      setError?.("An error occurred while fetching cost data.");
    } finally {
      setLoading(false);
    }
  }, [setError, setAvailableYears, selectedScenarios]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);


  // Handle Filtering based on Selected Years
  useEffect(() => {
    if (!fullResponse) return;

    if (selectedYears && selectedYears.length > 0) {
      const selectedYearValues = selectedYears.map((y: any) => y.value);
      const filteredRows: CostScenarioSummaryData[] = [];

      Object.values(fullResponse.scenarios || {}).forEach((scenario: any) => {
        // Find matching years
        const yearDataList = scenario.data_yearwise?.filter((d: any) => selectedYearValues.includes(d.year)) || [];

        // If multiple years selected, what to show in main row?
        // Using the FIRST match for now to keep consistent one-row-per-scenario view.
        // The detailed breakdown is in the accordion.
        if (yearDataList.length > 0) {
          const match = yearDataList[0];
          filteredRows.push({
            scenario_id: scenario.scenario_id,
            scenario_number: scenario.scenario_number,
            period: `Scenario ${scenario.scenario_number}`,
            re_gen_cost: match.re_gen_cost,
            non_re_gen_cost: match.non_re_gen_cost,
            cost_before_bess: match.cost_before_bess,
            cost_after_bess: match.cost_after_bess
          });
        }
      });

      if (filteredRows.length > 0) {
        setData(filteredRows);
      } else {
        setData([]);
      }

    } else {
      if (fullResponse.data_scenariowise) {
        setData(fullResponse.data_scenariowise);
      }
    }
  }, [selectedYears, fullResponse]);

  const handleRowClick = (index: number) => {
    setExpandedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const isRowExpanded = (index: number) => expandedRows.includes(index);

  const getYearlyDataForScenario = (scenarioNum: number) => {
    if (!scenariosData) return [];

    const scenarioId = data.find(d => d.scenario_number === scenarioNum)?.scenario_id;
    if (!scenarioId) return [];

    const scenario = scenariosData[scenarioId];
    if (!scenario) return [];

    let years = scenario.data_yearwise || [];

    if (selectedYears && selectedYears.length > 0) {
      const selectedYearValues = selectedYears.map((y: any) => y.value);
      years = years.filter((y: any) => selectedYearValues.includes(y.year));
    }

    return years;
  };


  return (
    <div
      className="overflow-auto capitalize h-full max-h-[260px]"
    >
      {loading ? (
        <div className="h-full w-full bg-white flex items-center justify-center">
          <img
            src={loader}
            width={200}
            alt="loader"
            className="block mt-[5rem]"
          />
        </div>
      ) : (
        <>
          {data.length === 0 ? (
            <div className="flex items-center justify-center overflow-auto capitalize rounded-lg w-full h-full">
              <h1 className="chartHeading !text-sm text-center !font-medium w-100">
                No Data Available...
              </h1>
            </div>
          ) : (
            <table id="cost-summary-table" className="w-full table-fixed">
              <thead className="sticky top-0 z-[1] bg-white">
                <tr className="text-sm border-gray-200">
                  <th className="py-2 text-xs px-2 text-left font-medium text-[#F1935C] w-[6%]"></th>
                  <HeaderCell className="w-[14%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        No. of Scenarios
                      </span>
                    }
                  />
                  <HeaderCell className="w-[20%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        RE Generation Cost (₹)
                      </span>
                    }
                  />
                  <HeaderCell className="w-[20%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        Non-RE Generation Cost (₹)
                      </span>
                    }
                  />
                  <HeaderCell className="w-[20%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        Cost Before BESS (₹)
                      </span>
                    }
                  />
                  <HeaderCell className="w-[20%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        Cost After BESS (₹)
                      </span>
                    }
                  />
                </tr>
              </thead>
              <tbody className="text-[#8C8A96] font-normal text-xs">
                {data.map((item, idx) => (
                  <>
                    <tr
                      key={idx}
                      className={`border-b border-gray-50 hover:bg-gray-50 cursor-pointer ${isRowExpanded(idx) ? "border-none" : ""
                        } ${idx % 2 === 0 ? "" : "!bg-[#FBFCFD]"}`}
                      onClick={() => handleRowClick(idx)}
                    >
                      <td className="py-2 px-2 text-center cursor-pointer">
                        {isRowExpanded(idx) ? (
                          <ChevronUp size={20} className="text-[#F1935C]" />
                        ) : (
                          <ChevronDown size={20} className="text-[#F1935C]" />
                        )}
                      </td>
                      <td className="py-2 px-2 text-center whitespace-normal font-medium text-[#F1935C]">
                        {item.scenario_number}
                      </td>
                      <td className="py-2 px-2 text-center whitespace-normal">
                        {formatCurrency(parseValue(item.re_gen_cost))}
                      </td>
                      <td className="py-2 px-2 text-center whitespace-normal">
                        {formatCurrency(parseValue(item.non_re_gen_cost))}
                      </td>
                      <td className="py-2 px-2 text-center whitespace-normal">
                        {formatCurrency(parseValue(item.cost_before_bess))}
                      </td>
                      <td className="py-2 px-2 text-center whitespace-normal">
                        {formatCurrency(parseValue(item.cost_after_bess))}
                      </td>
                    </tr>

                    {/* Expanded Yearly Data - Accordion Style */}
                    {isRowExpanded(idx) && (
                      <tr>
                        <td colSpan={6} className="bg-[#F7FAFF] p-2">
                          <div
                            className="capitalize text-nowrap !border !border-[#F1935C] rounded-xl overflow-hidden"
                          >
                            <table className="w-full table-fixed">
                              <tbody className="text-[#8C8A96] font-normal text-xs">
                                {getYearlyDataForScenario(item.scenario_number)
                                  .length > 0 ? (
                                  getYearlyDataForScenario(item.scenario_number).map(
                                    (yearItem: YearlyCostData, yearIdx: number) => (
                                      <tr
                                        key={yearIdx}
                                        className="text-center [&>td]:border-none bg-[#F7FAFF]"
                                      >
                                        <td className="py-2 px-2 text-center whitespace-normal font-medium text-[#34656D] w-[6%]"></td>
                                        <td className="py-2 px-2 text-center whitespace-normal font-medium text-[#34656D] w-[14%]">
                                          {yearItem.year}
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[20%]">
                                          {formatCurrency(
                                            parseValue(yearItem.re_gen_cost)
                                          )}
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[20%]">
                                          {formatCurrency(
                                            parseValue(yearItem.non_re_gen_cost)
                                          )}
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[20%]">
                                          {formatCurrency(
                                            parseValue(yearItem.cost_before_bess)
                                          )}
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[20%]">
                                          {formatCurrency(
                                            parseValue(yearItem.cost_after_bess)
                                          )}
                                        </td>
                                      </tr>
                                    )
                                  )
                                ) : (
                                  <tr>
                                    <td colSpan={5} className="text-center py-8">
                                      <h1 className="chartHeading !text-sm text-center !font-medium w-full">
                                        No Yearly Data Available...
                                      </h1>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}